import {
  getConfluenceService,
  type ConfluenceService,
} from '@canvas/atlassian-service/confluence';
import { currentHostFrameContext } from '@canvas/canvas-state/host-api';
import { type ExternalCommentId, type ReferenceId } from '@canvas/comments-types';
import { type FrameContext } from '@canvas/frame-api-types';

import { type CommentAnalyticsType } from '../comment-types.ts';

export type NotificationStoreEvent =
  /**
   * Indicates when a thread has been created remotely.
   * @description Use this as a signal to re-synchronise with the store.
   */
  { type: 'synchronised' };

export type NotificationStoreSubscriber = (event: NotificationStoreEvent) => void;

export class NotificationStore {
  private subscribers: NotificationStoreSubscriber[] = [];
  private viewedComments: Set<ExternalCommentId> | undefined;

  public constructor(
    private getBoardId: () => string,
    private service: ConfluenceService,
  ) {}

  public subscribe(subscription: NotificationStoreSubscriber) {
    this.subscribers.push(subscription);
  }

  public unsubscribe(subscription: NotificationStoreSubscriber) {
    this.subscribers = this.subscribers.filter((s) => s !== subscription);
  }

  public emit(event: NotificationStoreEvent) {
    this.subscribers.forEach((subscriber) => subscriber(event));
  }

  public markCommentsAsViewed(commentIds: ReferenceId[]) {
    if (!this.viewedComments) {
      return;
    }

    commentIds.forEach((commentId) => {
      const externalCommentId = this.toContentId(commentId);

      if (this.viewedComments?.has(commentId)) {
        return;
      }

      // We don't have to wait for this as it is going to be slow either way
      void this.service.markCommentsAsViewed({
        commentIds: [externalCommentId],
      });
    });
  }

  /**
   * Add comment reference ids to the viewed comments store.
   * Converts reference ids to external ids and adds them to the viewed comments store.
   * @param commentIds - The comment reference ids to add to the viewed comments store.
   */
  public addCommentReferenceIdsAsReadToStore(commentIds: ReferenceId[]) {
    if (!this.viewedComments) {
      return;
    }

    commentIds.forEach((commentId) => {
      const externalCommentId = this.toContentId(commentId);
      this.viewedComments?.add(externalCommentId);
    });

    this.emit({ type: 'synchronised' });
  }

  /**
   * Add comment external ids (also known as the reaction id) to the viewed comments store.
   * @param commentIds - The comment external ids to add to the viewed comments store.
   */
  public addCommentExternalIdsAsReadToStore(commentIds: ExternalCommentId[]) {
    if (!this.viewedComments) {
      return;
    }

    commentIds.forEach((commentId) => {
      this.viewedComments?.add(commentId);
    });

    this.emit({ type: 'synchronised' });
  }

  public async setViewedComments(contentType: CommentAnalyticsType) {
    const boardId = this.getBoardId();
    const viewedComments = await this.service.getViewedComments({
      contentId: boardId,
      contentType,
    });

    this.viewedComments = new Set(viewedComments.commentIds);
    this.emit({ type: 'synchronised' });
  }

  public getViewedComments() {
    return this.viewedComments;
  }

  private toContentId(referenceId: ReferenceId): ExternalCommentId {
    return this.service.toContentId(referenceId);
  }
}

let notificationStore: NotificationStore;

export const getNotificationStore = (
  service = getConfluenceService(),
  frameContext: FrameContext = currentHostFrameContext,
) => {
  if (!notificationStore) {
    notificationStore = new NotificationStore(
      () => frameContext.data.mediaCollectionId,
      service,
    );
  }

  return notificationStore;
};
